import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/types/types-external';
import { UnreadDocumentsCountsMap } from '../../../../model/documents/unread-documents-count';
import { retrieveUnreadDocumentsCounts } from '../common/common-thunks';
import {
  downloadContracts,
  downloadDispositions,
  downloadExpiredDocuments,
  toggleShowDocumentsType,
} from './documents-list-thunks';
import {
  DocumentsListState,
  DocumentsOrderCriteria,
} from './documents-list-types';

import { DocumentType } from '../../../../model';

const initialState: DocumentsListState = {
  // Section state
  contractsRequestStatus: 'idle',
  dispositionsRequestStatus: 'idle',
  expiredDocumentsRequestStatus: 'idle',

  // Filter
  filter: {
    orderCriteria: null,
    showUnreadDocuments: false,
    documentsFilters: {
      Contract: {
        show: true,
        unreadCount: 0,
      },
      Disposition: {
        show: false,
        unreadCount: 0,
      },
      ExpiredDocuments: {
        show: false,
        unreadCount: 0,
      },
    },
  },
};

const documentsListSlice = createSlice({
  name: 'features/documents/documentsList',
  initialState: initialState,
  reducers: {
    setOrderCriteria(state, data: PayloadAction<DocumentsOrderCriteria>) {
      state.filter = { ...state.filter, orderCriteria: data.payload };
    },
    setShowUnreadDocuments(state, data: PayloadAction<boolean>) {
      state.filter = { ...state.filter, showUnreadDocuments: data.payload };
    },
    updateUnreadCounts(state, data: PayloadAction<DocumentType>) {
      const cloneFilter = { ...state.filter };
      cloneFilter.documentsFilters[data.payload as any].unreadCount = Math.max(
        cloneFilter.documentsFilters[data.payload as any].unreadCount - 1,
        0
      );
      state.filter = cloneFilter;
    },
    markDocumentAsRead(
      state,
      data: PayloadAction<{ id: string; type: DocumentType }>
    ) {
      switch (data.payload.type) {
        case 'Contract': {
          const contracts = [...state.contracts];
          const contract = contracts.find(
            (item) => item.id === data.payload.id
          );
          contract.flagIsRead = true;
          state.contracts = contracts;
          break;
        }
        case 'Disposition': {
          const dispositions = [...state.dispositions];
          const disposition = dispositions
            .flatMap((d) => d.documents)
            .find((item) => item.id === data.payload.id);
          disposition.flagIsRead = true;
          state.dispositions = dispositions;
          break;
        }
        case 'ExpiredDocuments':
        default: {
          const expiredDocuments = [...state.expiredDocuments];
          const document = expiredDocuments
            .flatMap((d) => d.documents)
            .find((item) => item.id === data.payload.id);
          document.flagIsRead = true;
          state.expiredDocuments = expiredDocuments;
          break;
        }
      }
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(toggleShowDocumentsType.fulfilled, (state, action) => {
      state.filter.documentsFilters = action.payload;
    });
    builder.addCase(
      retrieveUnreadDocumentsCounts.fulfilled,
      (state, action) => {
        updateUnreadList(state, action.payload.counts);
      }
    );

    // Contracts
    builder.addCase(downloadContracts.pending, (state) => {
      state.contractsRequestStatus = 'loading';
      state.contracts = undefined;
    });
    builder.addCase(downloadContracts.rejected, (state, action) => {
      state.contractsRequestStatus = 'failed';
      state.downloadDocumentsError = action.payload;
    });
    builder.addCase(downloadContracts.fulfilled, (state, action) => {
      state.contractsRequestStatus = 'succeeded';
      state.contracts = action.payload;
    });

    // Dispositions
    builder.addCase(downloadDispositions.pending, (state) => {
      state.dispositionsRequestStatus = 'loading';
      state.dispositions = undefined;
    });
    builder.addCase(downloadDispositions.rejected, (state, action) => {
      state.dispositionsRequestStatus = 'failed';
      state.downloadDocumentsError = action.payload;
    });
    builder.addCase(downloadDispositions.fulfilled, (state, action) => {
      state.dispositionsRequestStatus = 'succeeded';
      state.dispositions = action.payload;
    });

    // Expired documents
    builder.addCase(downloadExpiredDocuments.pending, (state) => {
      state.expiredDocumentsRequestStatus = 'loading';
      state.expiredDocuments = undefined;
    });
    builder.addCase(downloadExpiredDocuments.rejected, (state, action) => {
      state.expiredDocumentsRequestStatus = 'failed';
      state.downloadDocumentsError = action.payload;
    });
    builder.addCase(downloadExpiredDocuments.fulfilled, (state, action) => {
      state.expiredDocumentsRequestStatus = 'succeeded';
      state.expiredDocuments = action.payload;
    });
  },
});

const updateUnreadList = (
  state: WritableDraft<DocumentsListState>,
  counts: UnreadDocumentsCountsMap
) => {
  Object.entries(counts).forEach(
    ([type, count]) => (state.filter.documentsFilters[type].unreadCount = count)
  );
};

export const documentsListActions = documentsListSlice.actions;

export default documentsListSlice.reducer;
